import { apiFetch } from "./api";

export interface BomItemSummary {
  bomItemId: number;
  name: string;
  bomItemTypeCode: string;
  calcType: string;
  calcFactor: number | null;
  unitCost: number | null;
  unitCurrencyCode: string;
  priceByRange: boolean;
  unitCostByRangeJson: string | null;
  supplierId: number | null;
  itemiseOnQuote: boolean;
  note: string | null;
}

// GET /api/bom-items returns a paged envelope { items, total }
interface BomItemPagedResult {
  items: BomItemSummary[];
  total: number;
}

export const bomItemsApi = {
  search: async (q: string): Promise<BomItemSummary[]> => {
    const result = await apiFetch<BomItemPagedResult>(`/api/bom-items?q=${encodeURIComponent(q)}`);
    return result.items;
  },
};

// ============================================================================
// Admin CRUD — BOM Plan III-A (Task 6)
//
// Backend (Plan I): GET /api/bom-items?type=&q=&supplierId=&page=&pageSize=
//   (0-based paging) → { items, total } of BomItemDto; GET /{id}; POST;
//   PATCH /{id} (If-Match); DELETE /{id} (If-Match). Claims bom-items:read|write|delete.
//
// Mirrors `productsAdminApi` (web/src/lib/products-admin.ts) for the If-Match idiom.
// ============================================================================

/** Read row — mirrors BomItemDto. INCLUDES the deferred range-pricing fields so
 *  they round-trip on READ. The concurrency token arrives as `rowVersion`. */
export interface BomItemRow {
  bomItemId: number;
  name: string;
  bomItemTypeCode: string;
  calcType: string;
  calcFactor: number | null;
  unitCost: number | null;
  unitCurrencyCode: string;
  priceByRange: boolean;
  unitCostByRangeJson: string | null;
  supplierId: number | null;
  itemiseOnQuote: boolean;
  note: string | null;
  // API serializes byte[] RowVersion as a base64 string under `rowVersion`
  // (BomItemDto has no separate base64 property, unlike Supplier/Category).
  rowVersion: string | null;
}

export interface BomItemAdminPage {
  items: BomItemRow[];
  total: number;
}

export interface BomItemListParams {
  type?: string;
  q?: string;
  supplierId?: number;
  page?: number;
  pageSize?: number;
}

/** Create — flat fields only (server defaults priceByRange=false). */
export interface CreateBomItemBody {
  name: string;
  bomItemTypeCode: string;
  calcType: string;
  calcFactor: number | null;
  unitCost: number | null;
  unitCurrencyCode: string | null;
  supplierId: number | null;
  itemiseOnQuote: boolean;
  note: string | null;
}

/** Update — range-pricing is now editable (III-B). Includes priceByRange +
 *  unitCostByRangeJson. Clear flags let the server set SupplierId/Note to null
 *  (the merge default leaves a null/absent field unchanged). */
export interface UpdateBomItemBody {
  name: string;
  bomItemTypeCode: string;
  calcType: string;
  calcFactor: number | null;
  unitCost: number | null;
  unitCurrencyCode: string | null;
  priceByRange: boolean;
  unitCostByRangeJson: string | null;
  supplierId: number | null;
  itemiseOnQuote: boolean;
  note: string | null;
  clearSupplierId?: boolean;
  clearNote?: boolean;
}

export const bomItemsAdminApi = {
  listAdmin: ({
    type,
    q,
    supplierId,
    page = 0,
    pageSize = 50,
  }: BomItemListParams): Promise<BomItemAdminPage> => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (type) params.set("type", type);
    if (q) params.set("q", q);
    if (supplierId != null) params.set("supplierId", String(supplierId));
    return apiFetch<BomItemAdminPage>(`/api/bom-items?${params.toString()}`);
  },
  get: (id: number): Promise<BomItemRow> => apiFetch<BomItemRow>(`/api/bom-items/${id}`),
  create: (body: CreateBomItemBody): Promise<BomItemRow> =>
    apiFetch<BomItemRow>("/api/bom-items", { method: "POST", body: JSON.stringify(body) }),
  update: (
    id: number,
    body: UpdateBomItemBody,
    rowVersionBase64: string | null,
  ): Promise<BomItemRow> =>
    apiFetch<BomItemRow>(`/api/bom-items/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      headers: rowVersionBase64 ? { "If-Match": rowVersionBase64 } : {},
    }),
  remove: (id: number, rowVersionBase64: string | null): Promise<void> =>
    apiFetch<void>(`/api/bom-items/${id}`, {
      method: "DELETE",
      headers: rowVersionBase64 ? { "If-Match": rowVersionBase64 } : {},
    }),
};

// ============================================================================
// Lookup helpers — co-located (small, single-domain). Endpoints require the
// `bom-lookups:read` claim. DTO shapes mirror BomLookupDtos.cs.
// ============================================================================

/** Mirrors BomItemTypeDto. */
export interface BomItemTypeOption {
  bomItemTypeCode: string;
  name: string;
}

/** Mirrors BomCalcTypeDto. */
export interface BomCalcTypeOption {
  calcType: string;
  calcName: string;
  description: string | null;
  canEditQuantity: boolean;
}

export const getBomItemTypes = (): Promise<BomItemTypeOption[]> =>
  apiFetch<BomItemTypeOption[]>("/api/bom-item-types");

export const getBomCalcTypes = (): Promise<BomCalcTypeOption[]> =>
  apiFetch<BomCalcTypeOption[]>("/api/bom-calc-types");
